import { motion } from 'framer-motion';
import { Route } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';

export default function ETADisplay({ prediccion, recorrido }) {
  if (!prediccion) {
    return (
      <div
        className="relative overflow-hidden rounded-2xl bg-card p-8 shadow-card border border-border/50"
        data-testid="eta-card"
      >
        <p className="text-center text-muted-foreground">
          No hay información disponible en este momento.
        </p>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          Vuelve a intentar en unos minutos.
        </p>
      </div>
    );
  }

  const etaClass =
    prediccion.etaMin <= 5
      ? 'text-success'
      : prediccion.etaMin <= 10
        ? 'text-primary'
        : 'text-foreground';

  const llegada = format(new Date(prediccion.etaTimestamp), 'HH:mm', { locale: es });

  const labelSentido = (s) => (s === 'ida' ? 'Ida' : s === 'vuelta' ? 'Vuelta' : null);
  const sentidoBusLabel = labelSentido(prediccion.sentidoBus);

  /** Minutos grandes + reloj: solo cuando el backend devuelve ETA real (GPS/polilínea). */
  const mostrarMinutosYReloj =
    prediccion.etaMin > 0 &&
    (prediccion.estadoServicio === 'OK' || prediccion.estadoServicio === 'Retraso');

  const esBusSinTransito = prediccion.etaContexto === 'bus_sin_transito';
  const esSinPatenteConfig = prediccion.etaContexto === 'sin_patente_configurada';
  const esSinGeometria = prediccion.etaContexto === 'sin_geometria';
  const esSinDatosReales = prediccion.etaContexto === 'sin_datos_reales';
  const esEtaTiempoReal = prediccion.etaContexto === 'proxima_parada';
  const esSoloItinerarioReferencia = prediccion.etaContexto === 'itinerario_referencia';

  /** Líneas con ETA por polilínea + GPS en backend (ida/vuelta). */
  const esRecorridoEtaPolilinea =
    recorrido?.id === 't1a' ||
    recorrido?.id === 't1b' ||
    recorrido?.id === 't2' ||
    recorrido?.id === 't3' ||
    recorrido?.id === 't4' ||
    recorrido?.id === 't5a' ||
    recorrido?.id === 't5b';
  const etiquetaLineaSinSentido =
    recorrido?.codigo || (recorrido?.id ? String(recorrido.id).toUpperCase() : null) || recorrido?.nombre || null;

  const badgeSinSentidoPolilinea = (() => {
    const ctx = prediccion.etaContexto;
    if (ctx === 'sin_ubicacion_bus') {
      return 'Sin iniciar recorrido';
    }
    if (ctx === 'sin_ruta') {
      return 'Ubicación fuera de la ruta (ida/vuelta no determinado)';
    }
    return 'Sentido de ruta no determinado';
  })();

  const tituloBloqueSuperior = esEtaTiempoReal
    ? 'Siguiente paradero'
    : prediccion.proximaParadaNombre && !mostrarMinutosYReloj
      ? 'Siguiente en itinerario'
      : 'Estimación de llegada';

  if (esBusSinTransito) {
    return (
      <div
        className="relative overflow-hidden rounded-2xl bg-card p-6 sm:p-8 shadow-card border border-border/50"
        data-testid="eta-card"
      >
        <div className="relative z-10" data-testid="bus-sin-transito">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-slate-500/15 px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide text-slate-800 dark:text-slate-200">
              Bus sin tránsito
            </span>
          </div>
          <p className="text-sm font-bold tracking-wide text-muted-foreground">Estado del vehículo</p>
          <p className="mt-2 text-lg font-bold leading-snug text-foreground">
            Detenido en aparcadero municipal
          </p>
        </div>
        <div className="relative z-10 mt-8 flex items-center gap-3 pt-6">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
            <Route className="h-5 w-5 text-primary" aria-hidden />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground/80">
              Distancia
            </p>
            <p className="truncate text-sm font-bold text-foreground" data-testid="distancia">
              —
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (esSinPatenteConfig) {
    return (
      <div
        className="relative overflow-hidden rounded-2xl bg-card p-6 sm:p-8 shadow-card border border-border/50"
        data-testid="eta-card"
      >
        <div className="relative z-10" data-testid="sin-patente-eta">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-amber-500/15 px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide text-amber-900 dark:text-amber-100">
              Patente no asignada
            </span>
          </div>
          <p className="text-sm font-bold tracking-wide text-muted-foreground">Este recorrido</p>
          <p className="mt-2 text-base leading-relaxed text-muted-foreground">
            No hay patente vinculada al trayecto en el sistema. En el panel admin asigná el bus a este recorrido (o
            configurá la variable de entorno del backend para T3). Sin patente no se puede leer el GPS del vehículo.
          </p>
        </div>
        <div className="relative z-10 mt-8 flex items-center gap-3 pt-6">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
            <Route className="h-5 w-5 text-primary" aria-hidden />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground/80">
              Distancia
            </p>
            <p className="truncate text-sm font-bold text-foreground" data-testid="distancia">
              —
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (esSinGeometria) {
    return (
      <div
        className="relative overflow-hidden rounded-2xl bg-card p-6 sm:p-8 shadow-card border border-border/50"
        data-testid="eta-card"
      >
        <div className="relative z-10" data-testid="sin-geometria-eta">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-destructive/15 px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide text-destructive">
              Ruta no cargada
            </span>
          </div>
          <p className="text-sm font-bold tracking-wide text-muted-foreground">Mapa del recorrido</p>
          <p className="mt-2 text-base leading-relaxed text-muted-foreground">
            El API no pudo leer los archivos KML del recorrido (vacíos o no encontrados en el contenedor). Deben estar en
            <span className="font-mono text-xs"> app/backend/data/kml/</span> o en la ruta{' '}
            <span className="font-mono text-xs">MUNIBUS_KML_DIR</span>.
          </p>
        </div>
        <div className="relative z-10 mt-8 flex items-center gap-3 pt-6">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
            <Route className="h-5 w-5 text-primary" aria-hidden />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground/80">
              Distancia
            </p>
            <p className="truncate text-sm font-bold text-foreground" data-testid="distancia">
              —
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (esSinDatosReales) {
    return (
      <div
        className="relative overflow-hidden rounded-2xl bg-card p-6 sm:p-8 shadow-card border border-border/50"
        data-testid="eta-card"
      >
        <div className="relative z-10" data-testid="sin-datos-reales-eta">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-muted px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide text-foreground/90">
              Sin tiempo estimado
            </span>
          </div>
          <p className="text-sm font-bold tracking-wide text-muted-foreground">Llegada al paradero</p>
          <p className="mt-2 text-base leading-relaxed text-muted-foreground">
            No hay datos del bus en tiempo real para calcular la llegada. Revisa el mapa o vuelve a intentar en unos
            minutos.
          </p>
        </div>
        <div className="relative z-10 mt-8 flex items-center gap-3 pt-6">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
            <Route className="h-5 w-5 text-primary" aria-hidden />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground/80">
              Distancia
            </p>
            <p className="truncate text-sm font-bold text-foreground" data-testid="distancia">
              —
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-card p-6 sm:p-8 shadow-card border border-border/50"
      data-testid="eta-card"
    >
      <div className="relative z-10">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {esSoloItinerarioReferencia ? (
            <span className="rounded-full bg-muted/80 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-foreground/90">
              Itinerario · orden de paradas
            </span>
          ) : esRecorridoEtaPolilinea && !sentidoBusLabel ? (
            <span className="rounded-full border border-dashed border-border px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              {badgeSinSentidoPolilinea}
            </span>
          ) : !esRecorridoEtaPolilinea ? (
            <span className="rounded-full bg-muted/70 px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide text-foreground/85">
              {etiquetaLineaSinSentido || 'Estimación de llegada'}
            </span>
          ) : null}
        </div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <p className="text-sm font-bold tracking-wide text-muted-foreground">{tituloBloqueSuperior}</p>
          {mostrarMinutosYReloj && (
            <div className="rounded-full border border-primary/15 bg-accent px-3 py-1.5 text-right shadow-sm">
              <span className="text-[10px] font-extrabold uppercase tracking-wide text-primary">Llegada </span>
              <span
                className="font-headline text-lg font-black tracking-tight text-accent-foreground"
                data-testid="eta-time"
              >
                {llegada}
              </span>
            </div>
          )}
        </div>
        {prediccion.proximaParadaNombre && (
          <p className="mb-2 text-base font-bold leading-snug text-foreground" data-testid="proxima-parada">
            {prediccion.proximaParadaNombre}
          </p>
        )}
        {mostrarMinutosYReloj && (
          <motion.div
            className="flex items-baseline gap-2"
            key={prediccion.etaMin}
            initial={{ scale: 1.06, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.28 }}
          >
            <span
              className={cn(
                'font-headline text-7xl font-black tracking-tighter sm:text-8xl eta-number outdoor-text',
                etaClass
              )}
              data-testid="eta-minutes"
            >
              {prediccion.etaMin}
            </span>
            <span className="font-headline text-xl font-bold italic text-primary">min</span>
          </motion.div>
        )}
        {esEtaTiempoReal && (
          <p className="mt-2 text-xs text-muted-foreground">Tiempo estimado al siguiente paradero</p>
        )}
      </div>

      <div className="relative z-10 mt-8 flex items-center gap-3 pt-6">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
          <Route className="h-5 w-5 text-primary" aria-hidden />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-extrabold uppercase tracking-widest text-muted-foreground/80">
            Distancia
          </p>
          <p className="truncate text-sm font-bold text-foreground" data-testid="distancia">
            {prediccion.distanciaKm != null ? `${prediccion.distanciaKm} km` : '—'}
          </p>
        </div>
      </div>
    </div>
  );
}
